<template>
  <div class="new-invoice concepts box wrapper-invoice-concepts">
    <div class="title-container is-flex">
      <h3 class="title is-centered is-4 has-text-primary title-invice-concetps">
        Conceptos
      </h3>
      <div class="field is-centered">
        <BTooltip
          label="Agregar partida"
          type="is-success"
        >
          <a
            v-if="!disabled"
            class="button add-concept is-success is-rounded"
            @click="addRow"
          >
            <BIcon icon="plus" />
          </a>
        </BTooltip>
      </div>
    </div>
    <BTable
      :data="concepts"
      :columns="columns"
      :opened-detailed="openedDetailed"
      detailed
      detail-key="id"
      @details-close="onDetailsClose"
    >
      <template
        slot="header"
        slot-scope="props"
      >
        <span :class="{ 'header-label': props.column.label === 'SKU' }">
          {{ props.column.label }}
        </span>
        <AppTooltip
          v-if="props.column.meta"
          :label="props.column.meta"
        />
      </template>
      <template slot-scope="props">
        <BTableColumn
          field="identificationNumber"
          internal
        >
          <AppSelectSearch
            name="identificationNumber"
            class="product-service-key-field"
            :class="{ 'selected-field': selected === `sku-${props.row.id}` }"
            placeholder="S012321A"
            :value="skuObj(props.row.identificationNumber, props.row.id)"
            :options="skuCatalog"
            :disabled="isLoading || disabled"
            :on-search="onSearchSku"
            :taggable="true"
            required
            @input="onChange($event, props.row.id)"
            @change="onChangeIdentificationNumber($event, props.row.id)"
            @focus="onChangeSelected(`sku-${props.row.id}`)"
            @blur="onClearSelected"
          />
        </BTableColumn>
        <BTableColumn
          field="description"
          internal
        >
          <AppSelectSearch
            name="description"
            placeholder="Refrigerador"
            class="product-service-key-field"
            :class="{ 'selected-field': selected === `description-${props.row.id}` }"
            :value="descriptionObject(props.row.description, props.row.id)"
            :options="descriptionCatalog"
            :disabled="isLoading || disabled"
            :on-search="onSearchDescription"
            :taggable="true"
            required
            @input="onChange($event, props.row.id)"
            @change="onChangeDescription($event, props.row.id)"
            @focus="onChangeSelected(`description-${props.row.id}`)"
            @blur="onClearSelected"
          />
        </BTableColumn>
        <BTableColumn
          field="productServiceKey"
          internal
        >
          <AppSelectSearch
            name="productServiceKey"
            class="product-service-key-field"
            :class="{ 'selected-field': selected === `product-service-key-${props.row.id}` }"
            placeholder="Clave de producto o servicio"
            :value="productServiceKeyObj(props.row.productServiceKey)"
            :options="productServiceKeyCatalog"
            :disabled="isLoading || disabled"
            :on-search="onSearch"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
            @focus="onChangeSelected(`product-service-key-${props.row.id}`)"
            @blur="onClearSelected"
          />
        </BTableColumn>
        <BTableColumn
          field="unitKey"
          internal
        >
          <AppSelectSearch
            name="unitKey"
            class="product-service-key-field"
            :class="{ 'selected-field': selected === `unit-key-${props.row.id}` }"
            placeholder="Clave de unidad"
            :options="unitKeyCatalog"
            :value="unitKeyObj(props.row.unitKey)"
            :disabled="isLoading || disabled"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
            @focus="onChangeSelected(`unit-key-${props.row.id}`)"
            @blur="onClearSelected"
          />
        </BTableColumn>
        <BTableColumn
          field="quantity"
          internal
        >
          <AppInputField
            name="quantity"
            placeholder="10"
            min="0.01"
            step="0.01"
            type="number"
            :disabled="isLoading || disabled"
            :value="props.row.quantity"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
          />
        </BTableColumn>
        <BTableColumn
          field="unit"
          internal
        >
          <AppInputField
            name="unit"
            placeholder="15.24"
            type="number"
            step="0.000001"
            min="0.000001"
            :disabled="isLoading || disabled"
            :value="props.row.unit"
            required
            @input="onChange($event, props.row.id)"
            @change="onChange($event, props.row.id)"
          />
        </BTableColumn>
        <BTableColumn
          field="taxes"
          internal
        >
          <div class="is-centered">
            <span>
              {{ taxesAmount(props.row) | toCurrency(voucherCurrency) }}
            </span>
            <a
              style="padding-left: .25rem; cursor: pointer;"
              @click="toggleDetail(props.row.id)"
            >
              <BIcon
                icon="pencil"
                type="is-primary"
              />
            </a>
          </div>
        </BTableColumn>
        <BTableColumn
          field="amount"
          internal
        >
          {{ amountWithTaxes(props.row) | toCurrency(voucherCurrency) }}
        </BTableColumn>
        <BTableColumn
          field="actions"
          internal
        >
          <span class="actions">
            <a
              v-if="concepts.length > 1"
              class="button is-danger"
              @click="deleteRow(props.row.id)"
            >
              <BIcon
                icon="delete"
                aria-hidden="true"
              />
            </a>
          </span>
        </BTableColumn>
      </template>
      <template
        slot="detail"
        slot-scope="detailProps"
      >
        <ConceptTaxes
          :id="detailProps.row.id"
          :unit="+detailProps.row.unit"
          :quantity="+detailProps.row.quantity"
          :tax-transfers="detailProps.row.taxTransfers"
          :retained-taxes="detailProps.row.retainedTaxes"
          :local-tax-transfers="detailProps.row.localTaxTransfers"
          :local-tax-with-holds="detailProps.row.localTaxWithHolds"
          :disabled="disabled"
        />
      </template>
      <template slot="footer">
        <th colspan="5" />
        <th>
          Totales
        </th>
        <th>
          {{ subTotalCurrency }}
        </th>
        <th>
          {{ taxesCurrency }}
        </th>
        <th>
          {{ totalCurrency }}
        </th>
        <tr
          v-if="isOpen"
          height="300px"
        />
      </template>
    </BTable>
  </div>
</template>

<script>
import merge from 'lodash/merge';
import debounce from 'lodash/debounce';

import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

import { keyUnit } from '@machete/common-catalogs';
import utils from '../../../../lib/utils';
import { conceptTaxes } from '../../../../store/modules/invoice/getters';
import SubjectToTaxTypes from '../../../../constants/SubjectToTaxTypes';

const calcTax = (unit = 0, quantity = 0) => (accum, { fee = 0 }) => accum + (
  (parseFloat(unit) * parseFloat(quantity)
  ) * parseFloat(fee));

export default {
  name: 'NewInvoiceConcepts',
  components: {
    AppTooltip: () => import(/* webpackChunkName: "app-tooltip" */ '@/components/AppTooltip.vue'),
    AppInputField: () => import(/* webpackChunkName: "app-input-field" */ '@/components/AppInputField'),
    AppSelectSearch: () => import(/* webpackChunkName: "app-select-search" */ '@/components/AppSelectSearch'),
    ConceptTaxes: () => import(/* webpackChunkName: "invoice-concept-taxes" */ '@/components/Admin/Invoices/NewInvoice/ConceptTaxes'),
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      selected: null,
      productServiceKeySearching: false,
      productServiceKeySearch: '',
      unitKeySearch: '',
      openedDetailed: [],
      skuSearch: {},
      columns: [
        {
          label: 'SKU',
          meta: 'Identificador de tu propio sistema de almacenamiento',
          field: 'identificationNumber',
          centered: true,
        },
        {
          label: 'Descripción',
          field: 'description',
          centered: true,
        },
        {
          label: 'Clave de producto o servicio',
          meta: 'Clave del producto correspondiente al cátalogo del SAT',
          field: 'productServiceKey',
          centered: true,
        },
        {
          label: 'Clave de unidad',
          meta: 'Clave de unidad de medida correspondiente al cátalogo del SAT',
          field: 'unitKey',
          centered: true,
        },
        {
          label: 'Cantidad',
          field: 'quantity',
          centered: true,
        },
        {
          label: 'Valor unitario',
          field: 'unit',
          centered: true,
        },
        {
          label: 'Impuestos',
          field: 'taxes',
          centered: true,
        },
        {
          label: 'Importe',
          field: 'amount',
          centered: true,
        },
        {
          label: '',
          field: 'actions',
          centered: true,
        },
      ],
      unitKeyCatalog: keyUnit.map(
        ({ key, value }) => ({ value: key, label: `${key} - ${value}` }),
      ),
      conceptTemplate: () => ({
        productServiceKey: '',
        identificationNumber: '',
        quantity: null,
        unitKey: '',
        description: '',
        unit: null,
        amount: null,
        taxTransfers: [
          {
            base: null,
            tax: '002',
            factorType: 'Tasa',
            fee: '0.160000',
            amount: null,
          },
        ],
        retainedTaxes: [],
        localTaxTransfers: [],
        localTaxWithHolds: [],
        subjectToTax: SubjectToTaxTypes.EnableTaxObject,
      }),
    };
  },
  computed: merge(
    mapState({
      productServiceKeyCatalog: ({ ui }) => ui.catalogs.productServiceKey,
      skuCatalog: ({ ui }) => ui.catalogs.sku,
      voucherCurrency: ({ invoice }) => invoice.voucherCurrency,
      concepts: ({ invoice }) => invoice.concepts,
      companyId: state => state.auth.companyId,
    }),
    mapGetters({
      totalCurrency: 'invoice/totalCurrency',
      subTotalCurrency: 'invoice/subTotalCurrency',
      taxesCurrency: 'invoice/taxesCurrency',
    }),
    {
      unitKeyObj() {
        return (unitKey) => {
          const value = this.unitKeyCatalog.find(
            ku => ku.value === unitKey,
          );
          if (value) {
            return value;
          }
          return unitKey;
        };
      },

      conceptAmount() {
        return ({ quantity = 0, unit = 0 }) => utils.standardRounding(
          parseFloat(unit) * parseFloat(quantity),
          this.voucherCurrency,
        );
      },

      taxesAmount() {
        return concept => conceptTaxes(concept, this.voucherCurrency);
      },

      amountWithTaxes() {
        return (concept) => {
          const subAmount = this.conceptAmount(concept);
          const taxesAmount = this.taxesAmount(concept);

          return utils.standardRounding(
            parseFloat(subAmount) + parseFloat(taxesAmount),
            this.voucherCurrency,
          );
        };
      },

      productServiceKeyObj() {
        return (productServiceKey) => {
          const value = this.productServiceKeyCatalog.find(
            psk => psk.value === productServiceKey,
          );
          if (value) {
            return value;
          }
          return productServiceKey;
        };
      },

      skuObj() {
        return (identificationNumber) => {
          const value = this.skuCatalog.find(
            sku => sku.value === identificationNumber,
          );

          if (value) return value;
          return identificationNumber;
        };
      },

      descriptionObject() {
        return (description) => {
          const value = this.descriptionCatalog.find(
            sku => sku.value === description,
          );

          if (value) return value;
          return description;
        };
      },

      descriptionCatalog() {
        const catalog = this.skuCatalog.map(
          ({ description, ...concept }) => ({
            ...concept,
            sku: concept.value,
            value: description,
            label: description,
          }),
        );

        return catalog;
      },
    },
  ),
  watch: {
    skuCatalog(newValue) {
      if (newValue.length > 2) this.isOpen = true;
    },
    descriptionCatalog(newValue) {
      if (newValue.length > 2) this.isOpen = true;
    },
    productServiceKeyCatalog(newValue) {
      if (newValue.length > 2) this.isOpen = true;
    },
  },
  methods: merge(
    mapMutations({
      setValue: 'invoice/updateConcept',
      setValues: 'invoice/updateConceptValues',
      addConcept: 'invoice/addConcept',
      deleteConcept: 'invoice/deleteConcept',
      addConceptTax: 'invoice/addConceptTax',
      deleteConceptTax: 'invoice/deleteConceptTax',
      setUiValue: 'ui/setValue',
    }),
    mapActions({
      getCatalog: 'ui/getCatalog',
      getCatalogSku: 'ui/getCatalogSku',
    }),
    {
      addRow() { this.addConcept(this.conceptTemplate()); },

      deleteRow(index) { this.deleteConcept(index); },

      taxesFromArray: (taxes, unit = 0, quantity = 0) => (
        Array.isArray(taxes) ? taxes.reduce(calcTax(unit, quantity), 0) : 0
      ),

      onChange(event, conceptId) {
        const { name, value } = event.target;

        const index = this.concepts.findIndex(({ id }) => id === conceptId);

        const accessor = `concepts.[${index}].${name}`;

        this.setValue({ accessor, value });
      },

      onChangeIdentificationNumber(event, conceptId) {
        const { label } = event;
        const { value } = event.target;

        const index = this.concepts.findIndex(({ id }) => id === conceptId);

        const sku = this.skuObj(value);

        if (sku.value) {
          this.setValues({
            index,
            values: {
              productServiceKey: sku.productServiceKey,
              unitKey: sku.unitKey,
              unit: sku.unitValue || 1,
              description: sku.description,
            },
          });
        }

        if (!value) {
          event.target = {
            name: 'identificationNumber',
            value: label,
          };
        }

        this.onChange(event, conceptId);
      },

      onChangeDescription(event = {}, conceptId) {
        const { label = '' } = event;
        const { value } = event.target;

        const index = this.concepts.findIndex(({ id }) => id === conceptId);

        const sku = this.descriptionObject(value);

        if (sku.value) {
          this.setValues({
            index,
            values: {
              identificationNumber: sku.sku,
              description: sku.description,
              productServiceKey: sku.productServiceKey,
              unitKey: sku.unitKey,
              unit: sku.unitValue || 1,
            },
          });
        }

        if (!value) {
          event.target = {
            name: 'description',
            value: label,
          };
        }

        this.onChange(event, conceptId);
      },

      onChangeProductServiceKey({ value }, index) {
        this.onChange({ target: { name: 'productServiceKey', value } }, index);
      },

      onChangeUnitKey({ value }, index) {
        this.onChange({ target: { name: 'unitKey', value } }, index);
      },

      getProductServicesKeys: debounce(async function getData() {
        this.productServiceKeySearching = true;

        await this.getCatalog({
          catalogName: 'productServiceKey',
          search: this.productServiceKeySearch,
        });

        this.productServiceKeySearching = false;
      }),

      onSearch(search, loading) {
        loading(true);

        this.search(loading, search, this);
      },

      onSearchSku(search, loading) {
        loading(true);

        this.searchSku(loading, search, this);
      },

      onSearchDescription(search, loading) {
        loading(true);

        this.searchByDescription(loading, search, this);
      },

      searchSku: debounce(async (loading, search, vm) => {
        await vm.getCatalogSku({
          catalogName: `sku/company/${vm.companyId}`,
          query: { sku: search },
        });

        loading(false);
      }, 650),

      searchByDescription: debounce(async (loading, search, vm) => {
        await vm.getCatalogSku({
          catalogName: `sku/company/${vm.companyId}`,
          query: { description: search },
        });

        loading(false);
      }, 650),

      search: debounce(async (loading, search, vm) => {
        await vm.getCatalog({ catalogName: 'productServiceKey', search });

        loading(false);
      }, 650),

      onDetailsClose(row) {
        const index = this.openedDetailed.indexOf(row.id);
        this.openedDetailed.slice(index, 1);
      },

      onChangeSelected(id) {
        if (id.includes('sku') && this.skuCatalog.length > 2) this.isOpen = true;
        if (id.includes('description') && this.descriptionCatalog.length > 2) this.isOpen = true;
        if (id.includes('product') && this.productServiceKeyCatalog.length > 2) this.isOpen = true;
        if (id.includes('unit')) this.isOpen = true;
        this.selected = id;
      },

      onClearSelected() {
        this.isOpen = false;
        this.selected = '';
      },

      toggleDetail(id) {
        if (this.openedDetailed.includes(id)) {
          const index = this.openedDetailed.indexOf(id);

          this.openedDetailed.pop(index);
        } else {
          this.openedDetailed.push(id);
        }
      },
    },
  ),
};
</script>

<style scoped>
.title.is-4 {
  margin-bottom: 0;
}

.new-invoice.concepts.box {
  overflow: auto;
}

.header-label {
  padding-right: 0.25rem;
}

.button.add-concept {
  padding-left: 0.75em!important;
  padding-right: 0.75em!important;
}

.button.add-concept .icon {
  margin-left: calc(-0.375em - 1px)!important;
  margin-right: calc(-0.375em - 1px)!important;
  font-size: inherit!important;
}

.add-concept-label {
  padding-left: .5rem;
}

.title-container {
  width: 100%;
  align-items: center;
  padding-bottom: .3rem;
  justify-content: space-between;
}

.product-service-key-field,
.product-service-key-field .field {
  width: 150px;
  max-width: 150px;
}

.selected-field.product-service-key-field,
.selected-field.product-service-key-field .field {
  width: 400px;
  max-width: 400px;
}

.unit-key-field,
.unit-key-field .field {
  width: 120px;
  max-width: 120px;
}

.selected-field.unit-key-field,
.selected-field.unit-key-field .field {
  width: 250px;
  max-width: 250px;
}

.description-field.selected-field {
  width: 400px;
}
</style>

<style>
.box.concepts th,
.box.concepts td {
  vertical-align: middle;
  text-align: center;
}

.box.concepts .label:not(:last-child) {
  margin-bottom: 0;
}

.new-invoice.concepts input[type='number'] {
  -moz-appearance: textfield;
}

.new-invoice.concepts input::-webkit-outer-spin-button,
.new-invoice.concepts input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.box.concepts .label:not(:last-child) {
  margin-right: 1rem;
}
</style>
